/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { mapActions, mapEnums, mapSelectors } from '@opengeoweb/store';
import type { Position, Size } from '@opengeoweb/webmap-react';
import { Clock } from '@opengeoweb/webmap-react';
import type { PopperPlacement } from './OptionsMenu';
import { getPopperplacement, OptionsMenu } from './OptionsMenu';

export const ClockConnect: FC<{ mapId: string }> = ({ mapId }) => {
  const dispatch = useDispatch();
  const [popperButtonElement, setPopperButtonElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperPlacement, setPopperPlacement] =
    useState<PopperPlacement>('bottom');

  const selectedTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getSelectedTime(store, mapId),
  );
  const isMapClockVisible = useSelector((store: CoreAppStore) =>
    mapSelectors.getIsClockVisible(store, mapId),
  );
  const clockPosition = useSelector((store: CoreAppStore) =>
    mapSelectors.getClockPosition(store, mapId),
  );
  const clockSize = useSelector((store: CoreAppStore) =>
    mapSelectors.getClockSize(store, mapId),
  );
  const isOptionsMenuVisible = useSelector((store: CoreAppStore) =>
    mapSelectors.getIsClockOptionsMenuVisible(store, mapId),
  );

  useEffect(() => {
    // Update optionsMenu placement when visiblity or clock position/size changes
    if (popperButtonElement) {
      setPopperPlacement(getPopperplacement(popperButtonElement, mapId));
    }
  }, [
    mapId,
    isOptionsMenuVisible,
    clockPosition,
    clockSize,
    popperButtonElement,
  ]);

  const onToggleClock = (isClockVisible: boolean): void => {
    dispatch(
      mapActions.toggleClock({
        mapId,
        isClockVisible,
        origin: mapEnums.MapActionOrigin.map,
      }),
    );
  };

  const onSetPosition = (position: Position): void => {
    dispatch(
      mapActions.setClockPositionSize({
        mapId,
        position,
        origin: mapEnums.MapActionOrigin.map,
      }),
    );
  };

  const onSetSize = (size: Size): void => {
    dispatch(
      mapActions.setClockPositionSize({
        mapId,
        size,
        origin: mapEnums.MapActionOrigin.map,
      }),
    );
  };

  const onToggleOptions = (): void => {
    dispatch(
      mapActions.toggleClockOptions({
        mapId,
        isClockOptionsMenuVisible: !isOptionsMenuVisible,
        origin: mapEnums.MapActionOrigin.map,
      }),
    );
  };

  return (
    <>
      <Clock
        isOptionsMenuVisible={isOptionsMenuVisible}
        isVisible={isMapClockVisible}
        onSetPosition={onSetPosition}
        onSetSize={onSetSize}
        onToggleClock={onToggleClock}
        onToggleOptions={onToggleOptions}
        optionsElement={setPopperButtonElement}
        position={clockPosition}
        size={clockSize}
        time={selectedTime}
      />
      <OptionsMenu
        buttonElement={popperButtonElement}
        isOpen={isOptionsMenuVisible}
        mapId={mapId}
        popperPlacement={popperPlacement}
      />
    </>
  );
};
